import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
  Row,
  RowSelectionState,
} from '@tanstack/react-table';
import { LinkRow, RowLink } from './rowLink';
import { 
  TableHeader, 
  TableBody, 
  TableHead, 
  TableRow, 
  TableCell,
  DropdownMenu,
  DropdownMenuItem
} from '../..';
import { 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreVertical,
} from 'lucide-react';
import { ErrorState } from '../state/ErrorState';
import { LoadingSpinner } from '../form/LoadingSpinner';
import { Button } from '../button/Button';
import { Pagination } from './Pagination';
import { SearchInput } from '../form/SearchInput';

export interface DataTableColumn<T> {
  id: string;
  header: string | (() => React.ReactNode);
  accessorKey?: keyof T;
  accessorFn?: (row: T) => any;
  cell: (row: T) => React.ReactNode;
  enableSorting?: boolean;
  sortingFn?: (rowA: Row<T>, rowB: Row<T>) => number;
  size?: number;
  isFlexible?: boolean;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  hideOnDesktop?: boolean;
  hideBeforeLargeDesktop?: boolean;
}

export interface DataTableAction<T> {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: (row: T) => void;
  variant?: 'default' | 'destructive';
  disabled?: (row: T) => boolean;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  actions?: DataTableAction<T>[];
  searchQuery?: string;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onRowClick?: (row: T) => void;
  /**
   * The standard way to make rows open a detail page. The whole row is
   * clickable, and one column's content (the first visible one, or
   * `rowLinkColumnId`) is wrapped in a real link to this address — so keyboard
   * focus, hover URL, and cmd/ctrl/middle-click "open in new tab" all work. A
   * click that ends a text selection, or lands on a control inside the row,
   * does not navigate. Keep that link column free of its own links/buttons (an
   * `<a>` can't nest). Supersedes
   * `onRowClick`; keep `onRowClick` for rows that act rather than navigate
   * (e.g. open a modal).
   */
  getRowHref?: (row: T) => string;
  /**
   * Which column holds the row's link (its text becomes the link's name).
   * Defaults to the first visible column; set it when that column is a status
   * chip, an icon or a button rather than the item's name. While this column
   * is hidden at the current width, no cell gets the link (the row click still
   * works) — falling back to another column could wrap the very button this
   * option was set to avoid.
   */
  rowLinkColumnId?: string;
  emptyMessage?: string;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  globalFilterFn?: (row: T, searchQuery: string) => boolean;
  className?: string;
  // Pagination props
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  enablePagination?: boolean;
  /**
   * Whether the table goes back to page 1 by itself whenever its rows, search or
   * sort change (the default). Pass `false` when the page number lives in the
   * URL: the reset also fires when the rows first load, so a link to `?page=2`
   * lands on page 1. A caller that turns it off must reset the page itself on a
   * new search or sort — `useClientDataTableUrl` already does.
   */
  autoResetPageIndex?: boolean;
  pageSizeOptions?: number[];
  // Loading state
  isLoading?: boolean;
  // Error state
  error?: string | null;
  onRetry?: () => void;
  // Row selection props
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  getRowId?: (row: T) => string;
  // Server-side pagination props (rendered inside DataTable)
  totalItems?: number;
  currentPage?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  // Built-in filter input props
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  filterPlaceholder?: string;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  actions = [],
  searchQuery = '',
  sorting,
  onSortingChange,
  onRowClick,
  getRowHref,
  rowLinkColumnId,
  emptyMessage = 'No data found.',
  emptyIcon: EmptyIcon,
  globalFilterFn,
  className,
  pagination,
  onPaginationChange,
  autoResetPageIndex,
  enablePagination = false,
  isLoading = false,
  error = null,
  onRetry,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  filterValue,
  onFilterChange,
  filterPlaceholder,
}: DataTableProps<T>) {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Listen for window resize to recalculate column widths
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter columns based on screen size
  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      if (windowWidth < 768 && column.hideOnMobile) return false;
      if (windowWidth < 1024 && column.hideOnTablet) return false;
      if (windowWidth < 1280 && column.hideBeforeLargeDesktop) return false;
      if (windowWidth >= 1280 && column.hideOnDesktop) return false;
      return true;
    });
  }, [columns, windowWidth]);

  // Create tanstack table columns
  const tableColumns: ColumnDef<T>[] = useMemo(() => {
    const cols: ColumnDef<T>[] = [];
    
    // Add selection column if enabled
    if (enableRowSelection) {
      cols.push({
        id: 'select',
        header: ({ table }: any) => {
          const isAllSelected = table.getIsAllRowsSelected();
          const isSomeSelected = table.getIsSomeRowsSelected();
          
          return (
            <div className="flex items-center justify-center">
              <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isSomeSelected && !isAllSelected;
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (isAllSelected || isSomeSelected) {
                      table.toggleAllRowsSelected(false);
                    } else {
                      table.toggleAllRowsSelected(true);
                    }
                  }}
                  className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-primary checked:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-white/10 dark:bg-white/5 dark:checked:border-primary dark:checked:bg-primary"
                  aria-label="Select all rows"
                />
                <svg
                  fill="none"
                  viewBox="0 0 14 14"
                  className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-primary-foreground"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:checked]:opacity-100"
                  />
                </svg>
              </div>
            </div>
          );
        },
        cell: ({ row }: any) => {
          return (
            <div className="flex items-center justify-center" data-row-link-ignore onClick={(e) => e.stopPropagation()}>
              <div className="group grid size-4 grid-cols-1">
                <input
                  type="checkbox"
                  checked={row.getIsSelected()}
                  onChange={(e) => {
                    e.stopPropagation();
                    row.toggleSelected();
                  }}
                  className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-primary checked:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-white/10 dark:bg-white/5 dark:checked:border-primary dark:checked:bg-primary"
                  aria-label={`Select row ${row.id}`}
                />
                <svg
                  fill="none"
                  viewBox="0 0 14 14"
                  className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-primary-foreground"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-0 group-has-[:checked]:opacity-100"
                  />
                </svg>
              </div>
            </div>
          );
        },
        enableSorting: false,
        size: 50,
        minSize: 50,
        maxSize: 50,
      });
    }
    
    // Add regular columns
    cols.push(...visibleColumns.map(column => ({
      id: column.id,
      ...(column.accessorKey && { accessorKey: column.accessorKey as string }),
      ...(column.accessorFn && { accessorFn: column.accessorFn }),
      header: ({ column: tableColumn }: any) => {
        // If header is a function, render it directly (no sorting support for custom headers)
        if (typeof column.header === 'function') {
          return column.header();
        }
        
        // Otherwise, render as string with optional sorting
        return column.enableSorting ? (
          <Button
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // For server-side sorting, we need to handle this manually
              if (!enablePagination) {
                // Server-side mode: determine next sort state based on controlled sorting prop
                const currentColumnSort = sorting?.find(s => s.id === column.id);
                const currentSort = currentColumnSort ? (currentColumnSort.desc ? 'desc' : 'asc') : false;
                let nextDesc: boolean;
                
                if (currentSort === false) {
                  // No sort → asc
                  nextDesc = false;
                } else if (currentSort === 'asc') {
                  // asc → desc  
                  nextDesc = true;
                } else {
                  // desc → clear (no sort)
                  if (onSortingChange) {
                    onSortingChange([]);
                  }
                  return;
                }
                
                // Apply new sort
                const newSorting = [{ id: column.id, desc: nextDesc }];
                if (onSortingChange) {
                  onSortingChange(newSorting);
                }
              } else {
                // Client-side mode: use default toggle behavior
                tableColumn.toggleSorting(tableColumn.getIsSorted() === 'asc');
              }
            }}
            className={`h-auto p-0 font-medium hover:bg-transparent ${
              tableColumn.getIsSorted() 
                ? 'text-primary hover:text-[hsl(var(--primary-hover))]' 
                : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {column.header}
            {tableColumn.getIsSorted() === 'asc' ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : tableColumn.getIsSorted() === 'desc' ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        ) : (
          <div className="font-medium text-gray-700 dark:text-gray-300">{column.header}</div>
        );
      },
      cell: ({ row }: any) => column.cell(row.original),
      enableSorting: column.enableSorting ?? true,
      ...(column.sortingFn && { sortingFn: column.sortingFn }),
      size: column.size || 150, // Use the explicit size directly without dynamic calculations
      minSize: column.size || 100,
      maxSize: column.size ? column.size * 2 : 500, // Allow some flexibility but keep it stable
    })));

    // Add actions column if actions are provided
    if (actions.length > 0) {
      cols.push({
        id: 'actions',
        header: () => <div className="w-8"></div>,
        cell: ({ row }: any) => (
          <div className="relative overflow-visible" style={{ overflow: 'visible' }} data-row-link-ignore onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              className="z-[9999]"
              menuClassName="z-[9999]"
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              }
            >
              {actions.map((action) => (
                <DropdownMenuItem
                  key={action.id}
                  onClick={() => action.onClick(row.original)}
                  destructive={action.variant === 'destructive'}
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </div>
        ),
        enableSorting: false,
        size: 24,
        minSize: 24,
        maxSize: 32,
      });
    }

    return cols;
  }, [visibleColumns, actions, windowWidth, enableRowSelection, sorting, onSortingChange, enablePagination]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      globalFilter: searchQuery,
      ...(enablePagination && pagination && { pagination }),
      ...(enableRowSelection && rowSelection !== undefined && { rowSelection }),
    },
    onSortingChange: (updaterOrValue: any) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(sorting);
        onSortingChange(newSorting);
      } else {
        onSortingChange(updaterOrValue);
      }
    },
    ...(autoResetPageIndex !== undefined && { autoResetPageIndex }),
    ...(enablePagination && onPaginationChange && {
      onPaginationChange: (updaterOrValue: any) => {
        if (typeof updaterOrValue === 'function') {
          onPaginationChange(updaterOrValue(pagination || { pageIndex: 0, pageSize: 10 }));
        } else {
          onPaginationChange(updaterOrValue);
        }
      },
    }),
    ...(enableRowSelection && onRowSelectionChange && {
      onRowSelectionChange: (updaterOrValue: any) => {
        if (typeof updaterOrValue === 'function') {
          const newSelection = updaterOrValue(rowSelection || {});
          onRowSelectionChange(newSelection);
        } else {
          onRowSelectionChange(updaterOrValue);
        }
      },
    }),
    ...(enableRowSelection && getRowId && { getRowId }),
    enableRowSelection: enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 100,
      maxSize: 500,
    },
    globalFilterFn: globalFilterFn ? (row: any, _columnId: any, filterValue: any) => {
      return globalFilterFn(row.original, filterValue);
    } : undefined,
  });

  const linkColumnId = rowLinkColumnId ?? visibleColumns[0]?.id;

  // A mistyped or renamed rowLinkColumnId silently leaves every row without its
  // real link (mouse clicks still work, so nobody notices). Say so. This only
  // fires on a misconfigured table, so it's not gated to development.
  // Keyed on the ids, not `columns` itself, which callers often pass inline.
  const columnIds = columns.map((c) => c.id).join('|');
  const hasRowHref = getRowHref != null;
  React.useEffect(() => {
    if (hasRowHref && rowLinkColumnId && !columnIds.split('|').includes(rowLinkColumnId)) {
      console.warn(`DataTable: rowLinkColumnId "${rowLinkColumnId}" matches no column; rows have no link.`);
    }
  }, [hasRowHref, rowLinkColumnId, columnIds]);

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Server-side pagination: consumer provides totalItems, currentPage, itemsPerPage, onPageChange
  // Show pagination during loading to avoid layout shift (totalItems may temporarily be 0 while refetching)
  const hasServerPaginationProps = totalItems != null && onPageChange != null && currentPage != null && itemsPerPage != null;
  const showServerPagination = hasServerPaginationProps
    && (Math.ceil(totalItems! / itemsPerPage!) > 1 || (isLoading && currentPage! > 1));

  // Client-side pagination: DataTable manages slicing via enablePagination + tanstack table
  const showClientPagination = enablePagination && table.getPageCount() > 1;

  const showFilter = onFilterChange != null;
  const showToolbar = showFilter || showServerPagination || showClientPagination;

  return (
    <div className={className || ''}>
      {showToolbar && (
        <div className="flex items-center justify-between mb-2 gap-4">
          {showFilter ? (
            <SearchInput
              value={filterValue ?? ''}
              onChange={onFilterChange!}
              onClear={() => onFilterChange!('')}
              placeholder={filterPlaceholder}
              className="w-full max-w-sm"
            />
          ) : (
            <div />
          )}
          {showServerPagination && (
            <Pagination
              currentPage={currentPage!}
              totalItems={totalItems!}
              itemsPerPage={itemsPerPage!}
              onPageChange={onPageChange!}
            />
          )}
          {showClientPagination && (
            <Pagination
              currentPage={(pagination?.pageIndex ?? 0) + 1}
              totalItems={table.getFilteredRowModel().rows.length}
              itemsPerPage={pagination?.pageSize ?? 10}
              onPageChange={(page) => {
                if (onPaginationChange) {
                  onPaginationChange({
                    pageIndex: page - 1,
                    pageSize: pagination?.pageSize ?? 10,
                  });
                }
              }}
            />
          )}
        </div>
      )}
      <div className="rounded-md border border-border">
      <div className="relative w-full overflow-x-auto rounded-md">
        <table className="w-full caption-bottom text-sm" style={{ tableLayout: 'fixed' }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header: any) => (
                <TableHead 
                  key={header.id}
                  className={`${
                    header.column.getIsSorted() 
                      ? 'bg-primary/5 dark:bg-primary/10 border-primary' 
                      : 'border-transparent'
                  }`}
                  style={{ 
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    maxWidth: header.column.columnDef.maxSize,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {error ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={tableColumns.length} className="h-32 py-8">
                <ErrorState
                  description={error}
                  action={onRetry && (
                    <Button
                      onClick={onRetry}
                    >
                      Try Again
                    </Button>
                  )}
                />
              </TableCell>
            </TableRow>
          ) : isLoading ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                <LoadingSpinner centerVertically />
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const href = getRowHref?.(row.original);
              const cells = row.getVisibleCells().map((cell) => (
                  <TableCell 
                    key={cell.id}
                    className={cell.column.id === 'actions' ? 'overflow-visible' : ''}
                    style={{ 
                      width: cell.column.getSize(),
                      minWidth: cell.column.getSize(),
                      maxWidth: cell.column.columnDef.maxSize,
                      ...(cell.column.id === 'actions' ? { overflow: 'visible' } : {}),
                      wordBreak: 'break-word',
                    }}
                  >
                    {href != null && cell.column.id === linkColumnId ? (
                      <RowLink href={href}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</RowLink>
                    ) : flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ));
              const rowState = row.getIsSelected() ? 'selected' : undefined;
              return href != null ? (
                <LinkRow as={TableRow} key={row.id} data-state={rowState} href={href}>
                  {cells}
                </LinkRow>
              ) : (
                <TableRow
                  key={row.id}
                  data-state={rowState}
                  className={onRowClick ? 'cursor-pointer' : undefined}
                  onClick={() => handleRowClick(row.original)}
                >
                  {cells}
                </TableRow>
              );
            })
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                <>
                  {EmptyIcon && (
                    <div className="flex flex-col items-center gap-2">
                      <EmptyIcon className="h-6 w-6 text-muted-foreground" />
                      <span>{emptyMessage}</span>
                    </div>
                  )}
                  {!EmptyIcon && emptyMessage}
                </>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </table>
      </div>
      </div>
    </div>
  );
}